/**
 * date-time-picker.js
 */

import React from 'react';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { Controller, useFormContext } from 'react-hook-form';
import { parseISO, getSeconds, isAfter, differenceInSeconds } from 'date-fns';
import get from 'lodash.get';


export default function PTDateTimePicker(props) {
  const {
    formContext: formContextProp,
    variant,
    name,
    rules,
    setValueAs,
    focusHandlers,
    defaultValue,
    minDate,
    maxDate,
    minDateMessage,
    maxDateMessage,
    showTodayButton,
    clearable,
    dataTestId,
    ...rest
  } = props;

  const theme = useTheme();
  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const error = get(formContext.formState.errors, name);
  
  const actions = ["cancel", "accept"]
  if (clearable) {
    actions.unshift("clear");
  }
  if (showTodayButton) {
    actions.unshift("today");
  }
  
  let Picker, minMaxPropSuffex = null;
  switch (variant) {
    case 'datetime':
      Picker = DateTimePicker;
      minMaxPropSuffex = 'DateTime';
      break;
    case 'date':
      Picker = DatePicker;
      minMaxPropSuffex = 'Date';
      break;
    case 'time':
      Picker = TimePicker;
      minMaxPropSuffex = 'Time';
      break;
    default:
      Picker = DateTimePicker;
      minMaxPropSuffex = 'DateTime';
      break;
  }

  const minMaxProps = {
    [`max${minMaxPropSuffex}`] : maxDate,
    [`min${minMaxPropSuffex}`]: minDate,
  }

  return (
    <Controller
      control={formContext.control}
      name={name}
      rules={{ 
        validate: {
          isValidDateTime: value => !(value && value.toString() === 'Invalid Date') || `Invalid ${variant} format`,
          validate: value => {
            if (maxDate || minDate) {
              const dateObj = typeof value === 'string' ? parseISO(value) : value;
              if (dateObj > maxDate) return maxDateMessage;
              if (dateObj < minDate) return minDateMessage;
            }
            return true;
          }
        },  
        ...rules 
      }}
      defaultValue={defaultValue}
      render={({ field: { ref, value, onChange, onBlur } }) => {

        const handleChange = (value) => {
          onChange((setValueAs && value) ? setValueAs(value) : value);
          formContext.trigger(name); //trigger validation
        };

        return (
          <Picker
            slotProps={{
              textField: {
                variant: 'outlined', 
                size: 'small',
                ref: ref,
                'data-testid': dataTestId ?? `${name}-${variant}-picker-input`,
                error: Boolean(error),
                helperText: error?.message,
                onBlur: (e) => {
                  onBlur(e);
                  if (focusHandlers) {
                    focusHandlers.onBlur(e);
                  }
                },
                onFocus: (e) => {
                  if (focusHandlers) {
                    focusHandlers.onFocus(e);
                  }
                },
              },
              field: { clearable: clearable, onClear: () => handleChange(null) },
              actionBar: { actions: actions },              
            }}
            localeText={{ todayButtonLabel: 'Set to Now' }}
            value={typeof value === 'string' ? parseISO(value) : value}
            onChange={date => {
              let dateVal = date;
              if (date) {
                // Set seconds to zero almost always. The only exception is when it's less than a minute
                // ago, which we'll assume (imperfectly) was set using the "today" button and not subsequently
                // adjusted. Non-zero seconds in this case is used by analytics, and understood to be not 100% 
                // reliable.
                dateVal = new Date(date.getTime())
                if (getSeconds(date) !== 0) {
                  const now = new Date();
                  if (isAfter(date, now) || differenceInSeconds(now, date) > 60) {
                    dateVal.setSeconds(0);
                  }
                }
              }
              handleChange(dateVal);
            }}
            closeOnSelect={false}
            {...minMaxProps}
            desktopModeMediaQuery={theme.breakpoints.up('sm')}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}      
            {...rest}
          />
        );
      }}
    />
  );
}